import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Navbar from "../components/Navbar";
import { Chart } from "chart.js/auto";

const fmt = (n) => new Intl.NumberFormat("es-CL").format(n || 0);

// --- util: YYYY-MM-DD -> Date (UTC-safe para solo fecha) ---
const toDate = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};

// --- util: semana basada en LUNES (0..N dentro del mes) ---
function weekIndexWithinMonth(dateObj) {
  const y = dateObj.getUTCFullYear();
  const m = dateObj.getUTCMonth();
  const first = new Date(Date.UTC(y, m, 1));
  const firstDow = first.getUTCDay(); // 0=Dom..6=Sab
  const firstMonday = new Date(first);
  const shift = (firstDow === 0 ? -6 : 1 - firstDow); // mover al lunes
  firstMonday.setUTCDate(first.getUTCDate() + shift);
  const diffDays = Math.floor((dateObj - firstMonday) / (1000 * 3600 * 24));
  return Math.floor(diffDays / 7);
}

// --- util: rango de fechas (YYYY-MM) por semana (Lun-Dom) ---
function computeWeekRanges(month) {
  const [y, m] = month.split("-").map(Number);
  const first = new Date(Date.UTC(y, m - 1, 1));
  const last = new Date(Date.UTC(y, m, 0));
  const firstDow = first.getUTCDay();
  const firstMonday = new Date(first);
  const shift = (firstDow === 0 ? -6 : 1 - firstDow);
  firstMonday.setUTCDate(first.getUTCDate() + shift);

  const weeks = [];
  let curStart = new Date(firstMonday);
  while (curStart <= last) {
    const curEnd = new Date(curStart);
    curEnd.setUTCDate(curStart.getUTCDate() + 6);
    const rangeStart = curStart < first ? first : curStart;
    const rangeEnd = curEnd > last ? last : curEnd;
    weeks.push({
      startISO: rangeStart.toISOString().slice(0, 10),
      endISO: rangeEnd.toISOString().slice(0, 10),
    });
    curStart = new Date(curStart);
    curStart.setUTCDate(curStart.getUTCDate() + 7);
  }
  return weeks;
}

const PronosticoMes = () => {
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [data, setData] = useState({
    month: "",
    days: [],            // [{ fecha: 'YYYY-MM-DD', total }]
    by_type: [],         // [{ almuerzo, total }]
    daily_by_type: []    // [{ fecha, almuerzo, total }]
  });

  const barRef = useRef(null);
  const barChart = useRef(null);

  const fetchData = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const resp = await axios.get("https://casinop2.sanjoseapps.cl/api/get_pronostico_mes.php", {
        params: { month } // YYYY-MM
      });
      if (resp.data?.ok) {
        setData(resp.data);
      } else {
        setData({ month, days: [], by_type: [], daily_by_type: [] });
        setErrorMsg(resp.data?.error || "No se pudo obtener el pronóstico.");
      }
    } catch (e) {
      setData({ month, days: [], by_type: [], daily_by_type: [] });
      setErrorMsg(e?.response?.data?.error || e.message || "Error de red.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [month]);

  // --- Series diarias ---
  const labels = useMemo(() => data.days.map(d => d.fecha.slice(-2)), [data.days]); // "01".."31"
  const totals = useMemo(() => data.days.map(d => Number(d.total || 0)), [data.days]);
  const totalMes = useMemo(() => totals.reduce((a,b)=>a+b,0), [totals]);

  // --- Resumen textual claro ---
  const diaMax = useMemo(() => {
    if (!data.days.length) return null;
    return data.days.reduce((mx, cur) => (Number(cur.total) > Number(mx.total) ? cur : mx), data.days[0]);
  }, [data.days]);

  const diaMin = useMemo(() => {
    if (!data.days.length) return null;
    return data.days.reduce((mn, cur) => (Number(cur.total) < Number(mn.total) ? cur : mn), data.days[0]);
  }, [data.days]);

  const promedioDia = useMemo(() => {
    if (!data.days.length) return 0;
    return Math.round(totalMes / data.days.length);
  }, [totalMes, data.days.length]);

  const masSolicitado = useMemo(() => {
    if (!data.by_type.length) return null;
    return data.by_type.reduce((max, cur) => (cur.total > (max?.total||0) ? cur : max), null);
  }, [data.by_type]);

  const menosSolicitado = useMemo(() => {
    if (!data.by_type.length) return null;
    return data.by_type.reduce((min, cur) => (cur.total < (min?.total??Infinity) ? cur : min), null);
  }, [data.by_type]);

  // --- Estadísticas semanales ---
  const weeks = useMemo(() => computeWeekRanges(month), [month]);

  const weeklyTotals = useMemo(() => {
    const map = new Map(); // idx -> total
    data.days.forEach(d => {
      const idx = weekIndexWithinMonth(toDate(d.fecha));
      map.set(idx, (map.get(idx) || 0) + Number(d.total || 0));
    });
    return map; // idx -> total
  }, [data.days]);

  const weeklyByType = useMemo(() => {
    const obj = {};
    data.daily_by_type.forEach(row => {
      const idx = weekIndexWithinMonth(toDate(row.fecha));
      obj[idx] ??= {};
      obj[idx][row.almuerzo] = (obj[idx][row.almuerzo] || 0) + Number(row.total || 0);
    });
    return obj;
  }, [data.daily_by_type]);

  // --- Dibujo del gráfico de barras (solo diario) ---
  useEffect(() => {
    if (barChart.current) { barChart.current.destroy(); barChart.current = null; }
    if (barRef.current && labels.length) {
      barChart.current = new Chart(barRef.current, {
        type: "bar",
        data: {
          labels,
          datasets: [{
            label: "Almuerzos programados por día",
            data: totals
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: { callbacks: { label: (ctx) => `${fmt(ctx.raw)} alm.` } }
          },
          scales: {
            x: { title: { display: true, text: "Día del mes" }, ticks: { maxRotation: 0 } },
            y: { title: { display: true, text: "Cantidad de almuerzos" }, beginAtZero: true, ticks: { precision:0 } }
          },
          elements: { bar: { borderRadius: 6 } }
        }
      });
    }
  }, [labels, totals]);

  return (
    <>
      <Navbar />
      <section className="section">
        <div className="container">

          {/* Título estilo dashboard */}
          <h1 className="title" style={{ fontSize: 28, fontWeight: 800, marginBottom: 4 }}>
            Pronóstico mensual
          </h1>
          <p className="subtitle" style={{ fontSize: 14, color: "#6b7280", marginTop: 0 }}>
            Vista de programación por día y resumen por semana. Selecciona el mes para actualizar las métricas.
          </p>

          {/* KPIs superiores */}
          <div className="columns is-multiline">
            <div className="column is-3">
              <div className="box" style={{ borderRadius: 16 }}>
                <div style={{ fontSize: 12, color: "#6b7280" }}>Total del mes</div>
                <div style={{ fontSize: 28, fontWeight: 800 }}>{fmt(totalMes)} alm.</div>
              </div>
            </div>
            <div className="column is-3">
              <div className="box" style={{ borderRadius: 16 }}>
                <div style={{ fontSize: 12, color: "#6b7280" }}>Promedio diario</div>
                <div style={{ fontSize: 28, fontWeight: 800 }}>{fmt(promedioDia)} alm./día</div>
              </div>
            </div>
            <div className="column is-3">
              <div className="box" style={{ borderRadius: 16 }}>
                <div style={{ fontSize: 12, color: "#6b7280" }}>Pico del mes</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>
                  {diaMax ? `${diaMax.fecha}` : "-"}
                </div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  {diaMax ? `${fmt(diaMax.total)} alm.` : ""}
                </div>
              </div>
            </div>
            <div className="column is-3">
              <div className="box" style={{ borderRadius: 16 }}>
                <div style={{ fontSize: 12, color: "#6b7280" }}>Mes seleccionado</div>
                <input
                  className="input"
                  type="month"
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                />
                {loading && <div className="tag is-warning is-light" style={{ marginTop: 8 }}>Actualizando…</div>}
              </div>
            </div>
          </div>

          <div className="columns is-multiline">
            <div className="column is-3">
              <div className="box" style={{ borderRadius: 16, height: "100%" }}>
                <div style={{ fontSize: 12, color: "#6b7280" }}>Día más bajo</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>
                  {diaMin ? `${diaMin.fecha}` : "-"}
                </div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  {diaMin ? `${fmt(diaMin.total)} alm.` : ""}
                </div>
              </div>
            </div>
            <div className="column is-9">
              {/* Gráfico de barras diario */}
              <div className="box" style={{ borderRadius: 16 }}>
                <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Almuerzos programados por día</div>
                <p className="help" style={{ marginTop: -6 }}>Suma de almuerzos para cada fecha del mes seleccionado.</p>
                <div style={{ height: 360 }}>
                  <canvas ref={barRef} />
                </div>
              </div>
            </div>
          </div>

          {/* Preferencias por tipo (texto + tabla) */}
          <div className="box" style={{ borderRadius: 16 }}>
            <div className="columns">
              <div className="column is-5">
                <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>Preferencias por tipo</div>
                <p style={{ fontSize: 14, color: "#6b7280" }}>
                  Tipos más y menos solicitados según la programación del mes.
                </p>
                <div className="content" style={{ marginTop: 12 }}>
                  <p><strong>Más solicitado:</strong> {masSolicitado ? `${masSolicitado.almuerzo} — ${fmt(masSolicitado.total)} alm.` : "-"}</p>
                  <p><strong>Menos solicitado:</strong> {menosSolicitado ? `${menosSolicitado.almuerzo} — ${fmt(menosSolicitado.total)} alm.` : "-"}</p>
                </div>
              </div>
              <div className="column is-7">
                <table className="table is-fullwidth is-striped">
                  <thead>
                    <tr><th>Tipo de almuerzo</th><th className="has-text-right">Total en el mes</th></tr>
                  </thead>
                  <tbody>
                    {data.by_type.map((t, i) => (
                      <tr key={i}>
                        <td>{t.almuerzo}</td>
                        <td className="has-text-right">{fmt(t.total)}</td>
                      </tr>
                    ))}
                    {!data.by_type.length && (
                      <tr><td colSpan="2" className="has-text-centered">Sin datos</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Estadísticas semanales */}
          <div className="box" style={{ borderRadius: 16 }}>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>Resumen semanal (Lunes a Domingo)</div>
            <p className="help" style={{ marginTop: -6 }}>
              Cada fila representa una semana que cae (parcial o totalmente) dentro del mes seleccionado.
            </p>
            <div style={{ overflowX: "auto" }}>
              <table className="table is-fullwidth is-striped is-hoverable">
                <thead>
                  <tr>
                    <th># Semana</th>
                    <th>Rango de fechas</th>
                    <th className="has-text-right">Total de almuerzos</th>
                    <th>Detalle por tipo (en la semana)</th>
                  </tr>
                </thead>
                <tbody>
                  {computeWeekRanges(month).map((w, idx) => {
                    const totalW = weeklyTotals.get(idx) || 0;
                    const byTypeW = weeklyByType[idx] || {};
                    const tipos = Object.keys(byTypeW);
                    return (
                      <tr key={idx}>
                        <td>{idx + 1}</td>
                        <td>{w.startISO} — {w.endISO}</td>
                        <td className="has-text-right">{fmt(totalW)}</td>
                        <td>
                          {tipos.length
                            ? tipos.map((k) => (
                                <span key={k} className="tag is-light" style={{ marginRight: 6, marginBottom: 6 }}>
                                  {k}: {fmt(byTypeW[k])}
                                </span>
                              ))
                            : <span className="has-text-grey">Sin datos</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {errorMsg && <div className="notification is-danger">{errorMsg}</div>}

        </div>
      </section>
    </>
  );
};

export default PronosticoMes;
